package com.heelion.farmafreelance.agent

import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.heelion.farmafreelance.models.AnnuncioStato
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.CreatoreTipo
import com.heelion.farmafreelance.models.Preferito
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.StoricoEntry
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.Notification
import com.heelion.farmafreelance.models.SubscriptionType
import com.heelion.farmafreelance.models.UserReputation
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.UserStatus
import com.heelion.farmafreelance.models.isProfessionista

/** Istantanea dei dati dell'utente già caricati nell'app, su cui l'agente risponde. */
data class ContestoUtente(
    val ruolo: UserRole?,
    val matches: List<Match> = emptyList(),
    val notifiche: List<Notification> = emptyList(),
    val abbonamento: UserStatus? = null,
    /** false finché l'app è gratuita: Farmy non parla di scadenze né di Premium. */
    val abbonamentiAttivi: Boolean = true,
    val reputazione: UserReputation? = null,
    val preferiti: List<Preferito> = emptyList(),
    /** Promemoria di Farmy attivati dall'utente. */
    val promemoria: PreferenzePromemoria = PreferenzePromemoria(),
    /** Zona su cui si cercano i turni "vicino a me" (es. "MI" o "MI, BG"); null se non impostata. */
    val zona: String? = null,
    /** Annunci dei turni confermati (orari e compenso), per id annuncio: per i guadagni previsti. */
    val dettagliConfermati: Map<String, AnnuncioTurno> = emptyMap(),
    /** Percentuale della cassa nel profilo del professionista (ENPAF 2.0, ENPAPI 4.0). */
    val percEnpaf: Double = 2.0,
    /** La cassa del professionista: "ENPAF" per i farmacisti, "ENPAPI" per gli infermieri. */
    val cassa: String = "ENPAF",
    /** Aliquota del forfettario (es. 15.0) per stimare l'imposta; null se il regime non è forfettario. */
    val aliquotaImposta: Double? = null,
    /** Coordinate (lat, lng) dell'indirizzo del profilo, per le emergenze "vicino a me"; null se non note. */
    val posizione: Pair<Double, Double>? = null,
    /** Farmacia con gli infermieri attivi: lo spazio aperto (farmacisti o infermieri); null altrimenti. */
    val professioneCercata: Professione? = null
) {
    companion object {
        val VUOTO = ContestoUtente(ruolo = null)
    }
}

/**
 * Costruisce le risposte alle domande dell'utente SOLO dai suoi dati reali, con frasi a
 * template: nessun testo generato, quindi nessuna risposta inventata. Il modello decide
 * soltanto quale domanda è stata fatta ([TipoDomanda]); i numeri li calcola questo codice.
 */
object RisponditoreDomande {

    /**
     * @param annunci turni aperti delle controparti, solo per i [TipoDomanda.richiedeAnnunci]
     *   (li carica il chiamante dalla rete); null = non disponibili (es. errore di connessione).
     */
    fun rispondi(
        tipo: TipoDomanda,
        ctx: ContestoUtente,
        oggi: LocalDate,
        tz: TimeZone = TimeZone.currentSystemDefault(),
        annunci: List<AnnuncioTurno>? = null,
        /** Frase dell'utente, per i dettagli (es. il periodo dei guadagni: "a ottobre"). */
        testo: String = "",
        /** Storico dei turni svolti, per [TipoDomanda.richiedeStorico]; null = non disponibile. */
        storico: List<StoricoEntry>? = null,
        /** Per le emergenze (turni che iniziano entro 24 ore). */
        adesso: Instant = kotlinx.datetime.Clock.System.now(),
        /** Turni o disponibilità pubblicati dall'utente, per [TipoDomanda.richiedeMieiAnnunci]; null = non disponibili. */
        mieiAnnunci: List<AnnuncioTurno>? = null
    ): String = when (tipo) {
        TipoDomanda.PROSSIMO_TURNO -> prossimoTurno(ctx, oggi, tz)
        TipoDomanda.STATO_TURNI -> statoTurni(ctx, testo)
        TipoDomanda.NOTIFICHE -> notifiche(ctx)
        TipoDomanda.ABBONAMENTO -> abbonamento(ctx, tz)
        TipoDomanda.REPUTAZIONE -> reputazione(ctx)
        TipoDomanda.AIUTO -> if (ctx.abbonamentiAttivi) AIUTO else AIUTO.replace("\"quando scade l'abbonamento?\", ", "")
        TipoDomanda.TURNI_DISPONIBILI -> turniDisponibili(ctx, annunci, oggi, tz, testo)
        TipoDomanda.TURNI_PREFERITI -> turniPreferiti(ctx, annunci, oggi, tz)
        TipoDomanda.RIEPILOGO -> riepilogo(ctx, oggi, tz)
        TipoDomanda.PROMEMORIA_ATTIVI -> EstrattorePromemoria.elenco(ctx.promemoria)
        TipoDomanda.GUADAGNI -> guadagni(ctx, oggi, testo, storico)
        TipoDomanda.PAGAMENTI -> when {
            ctx.ruolo?.isProfessionista != true -> "Il resoconto dei pagamenti è per farmacisti e infermieri."
            storico == null -> "Non riesco a caricare i turni svolti in questo momento: controlla la connessione e riprova."
            else -> ResocontoPagamenti.risposta(storico)
        }
        TipoDomanda.FARMACIE_PIU_PAGANTI -> ClassificaFarmacie.risposta(ctx.ruolo, storico)
        TipoDomanda.TURNI_PIU_PAGATI -> TurniMeglioPagati.risposta(ctx, annunci, testo, oggi, tz)
        TipoDomanda.EMERGENZE -> EmergenzeFarmy.risposta(ctx, annunci, oggi, tz, adesso)
        TipoDomanda.TURNI_VICINI -> TurniVicini.risposta(ctx, annunci, testo, oggi, tz)
        TipoDomanda.AGENDA -> AgendaTurni.risposta(ctx, storico, oggi)
        TipoDomanda.TURNO_COPERTO -> DomandeCalendario.coperto(ctx, mieiAnnunci, testo, oggi, tz)
        TipoDomanda.TURNI_PUBBLICATI -> DomandeCalendario.pubblicati(ctx, mieiAnnunci, testo, oggi, tz)
        TipoDomanda.SONO_LIBERO -> DomandeCalendario.libero(ctx, testo, oggi, tz)
    }

    /**
     * Provincia con cui chiedere al server i turni aperti per [tipo] (null = tutta Italia):
     * le emergenze e i turni "vicino a me" con la posizione nota si cercano ovunque e si ordinano per distanza.
     */
    fun provinciaAnnunci(tipo: TipoDomanda, ctx: ContestoUtente, testo: String): String? = when (tipo) {
        TipoDomanda.TURNI_DISPONIBILI -> ctx.zona
        TipoDomanda.TURNI_PIU_PAGATI, TipoDomanda.TURNI_VICINI -> TurniVicini.luogo(testo, ctx.zona).let { luogo ->
            val perDistanza = (tipo == TipoDomanda.TURNI_VICINI || DomandeTurni.vicino(testo)) && TurniVicini.usaDistanza(ctx, luogo)
            if (perDistanza) null else luogo.provincia
        }
        TipoDomanda.EMERGENZE -> if (ctx.posizione != null) null else ctx.zona
        else -> null
    }

    private fun guadagni(ctx: ContestoUtente, oggi: LocalDate, testo: String, storico: List<StoricoEntry>?): String {
        if (ctx.ruolo?.isProfessionista != true) return "Il riepilogo dei guadagni è per farmacisti e infermieri."
        if (storico == null) return "Non riesco a caricare lo storico dei turni in questo momento: controlla la connessione e riprova."
        val periodo = PeriodoGuadagni.estrai(testo, oggi)
        val r = CalcolatoreGuadagni.riepilogo(
            CalcolatoreGuadagni.voci(storico, ctx.matches, ctx.dettagliConfermati, oggi), periodo, ctx.percEnpaf, ctx.aliquotaImposta
        )
        if (r.turni == 0) return "${periodo.descrizione()} non hai turni svolti o confermati."
        val ore = if (r.ore % 1.0 == 0.0) r.ore.toInt().toString() else r.ore.toString().replace('.', ',')
        val imposta = r.imposta?.let { imp ->
            " Imposta sostitutiva stimata (forfettario ${formattaPerc(r.aliquota ?: 0.0)}) ${formattaEuro(imp)}, " +
                "netto stimato ${formattaEuro(r.nettoDopoImposte ?: r.netto)}: è una stima, verificala col commercialista."
        }.orEmpty()
        return "${periodo.descrizione()}: ${plurale(r.turni, "turno", "turni")}, $ore ore, " +
            "lordo ${formattaEuro(r.lordo)}, ${ctx.cassa} stimato (${formattaPerc(ctx.percEnpaf)}) ${formattaEuro(r.enpaf)}, " +
            "netto dopo ${ctx.cassa} ${formattaEuro(r.netto)}." + imposta +
            if (r.lordoPrevisto > 0) " Di questi, ${formattaEuro(r.lordoPrevisto)} sono di turni ancora da svolgere." else ""
    }

    private fun formattaPerc(p: Double) = (if (p % 1.0 == 0.0) p.toInt().toString() else p.toString().replace('.', ',')) + "%"

    private fun turniDisponibili(ctx: ContestoUtente, annunci: List<AnnuncioTurno>?, oggi: LocalDate, tz: TimeZone, testo: String = ""): String {
        val mio = ctx.ruolo?.creatoreTipo() ?: return "Accedi per vedere i turni disponibili."
        val proposte = conteggi(ctx.matches, mio).proposte
        val inizio = if (proposte > 0) {
            "Hai ${plurale(proposte, "proposta ricevuta", "proposte ricevute")} da valutare nella Home. "
        } else ""
        if (annunci == null) return inizio + NON_CARICATI
        // "ci sono turni nel weekend?": solo quelli del giorno o periodo detto.
        val (nuovi, quando) = DomandeCalendario.nelPeriodo(annunciNuovi(ctx, annunci, oggi, tz), testo, oggi, tz)
        val dove = (ctx.zona?.let { " nella tua zona ($it)" }
            ?: " (non hai impostato la zona nel profilo: ti mostro tutta Italia)") + quando?.let { " $it" }.orEmpty()
        if (nuovi.isEmpty()) return inizio + "Al momento non ci sono nuovi turni aperti di ${controparti(mio)}$dove."
        return inizio + "Ci sono ${plurale(nuovi.size, "turno aperto", "turni aperti")} di ${controparti(mio)}$dove: " +
            elenco(nuovi, oggi, tz)
    }

    private fun turniPreferiti(ctx: ContestoUtente, annunci: List<AnnuncioTurno>?, oggi: LocalDate, tz: TimeZone): String {
        val mio = ctx.ruolo?.creatoreTipo() ?: return "Accedi per vedere i turni dei tuoi preferiti."
        val idPreferiti = ctx.preferiti.map { it.targetId }.toSet()
        if (idPreferiti.isEmpty()) {
            return "Non hai ancora preferiti: aggiungili toccando il cuore sul profilo di una " +
                (if (mio != CreatoreTipo.FARMACIA) "farmacia" else "farmacista o di un infermiere") + "."
        }
        if (annunci == null) return NON_CARICATI
        val daiPreferiti = annunciNuovi(ctx, annunci, oggi, tz).filter { it.creatoreId in idPreferiti }
        if (daiPreferiti.isEmpty()) {
            return "I tuoi ${plurale(idPreferiti.size, "preferito", "preferiti")} non hanno turni aperti al momento."
        }
        return "I tuoi preferiti hanno ${plurale(daiPreferiti.size, "turno aperto", "turni aperti")}: " +
            elenco(daiPreferiti, oggi, tz)
    }

    /**
     * Promemoria per il saluto all'apertura dell'app: turni confermati dei prossimi 7 giorni,
     * turni da confermare e proposte a cui rispondere. null se non c'è niente da ricordare.
     */
    fun promemoria(ctx: ContestoUtente, oggi: LocalDate, tz: TimeZone = TimeZone.currentSystemDefault()): String? {
        val mio = ctx.ruolo?.creatoreTipo() ?: return null
        val parti = mutableListOf<String>()
        prossimiImpegni(ctx, oggi, tz)?.let { parti += "Ti ricordo i tuoi impegni: $it." }
        val c = conteggi(ctx.matches, mio)
        val daFare = listOfNotNull(
            c.daConfermare.takeIf { it > 0 }?.let { "${plurale(it, "turno", "turni")} da confermare" },
            c.proposte.takeIf { it > 0 }?.let { "${plurale(it, "proposta", "proposte")} a cui rispondere" }
        )
        if (daFare.isNotEmpty()) parti += "Hai " + daFare.joinToString(" e ") + "."
        return parti.takeIf { it.isNotEmpty() }?.joinToString(" ")
    }

    /** "oggi alla Farmacia Rossi; sabato 27 settembre alla Farmacia Bianchi" (max 3), o null. */
    private fun prossimiImpegni(ctx: ContestoUtente, oggi: LocalDate, tz: TimeZone, giorni: Int = 7): String? {
        val prossimi = ctx.matches
            .filter { it.stato == MatchStato.CONFERMATO }
            .mapNotNull { m -> m.dataTurno?.toLocalDateTime(tz)?.date?.let { it to m } }
            .filter { (d, _) -> (d.toEpochDays() - oggi.toEpochDays()) in 0 until giorni }
            .sortedBy { it.first }
        if (prossimi.isEmpty()) return null
        val elenco = prossimi.take(3).joinToString("; ") { (d, m) ->
            val quando = when (d.toEpochDays() - oggi.toEpochDays()) {
                0 -> "oggi"
                1 -> "domani"
                else -> formattaGiorno(d, oggi)
            }
            quando + (m.nomeControparte?.let { " alla $it" } ?: "")
        }
        val altri = prossimi.size - 3
        return elenco + if (altri > 0) "; e ${plurale(altri, "altro", "altri")}" else ""
    }

    /** Le cose da fare, in ordine di urgenza: pensata come prima domanda del giorno. */
    private fun riepilogo(ctx: ContestoUtente, oggi: LocalDate, tz: TimeZone): String {
        val mio = ctx.ruolo?.creatoreTipo() ?: return "Accedi per vedere cosa c'è da fare."
        val voci = mutableListOf<String>()

        prossimiImpegni(ctx, oggi, tz)?.let { voci += "i tuoi prossimi turni sono $it" }
        val c = conteggi(ctx.matches, mio)
        if (c.proposte > 0) voci += "${plurale(c.proposte, "proposta ricevuta", "proposte ricevute")} a cui rispondere"
        if (c.daConfermare > 0) voci += "${plurale(c.daConfermare, "turno", "turni")} da confermare"
        val nonLette = ctx.notifiche.count { !it.isRead }
        if (nonLette > 0) voci += plurale(nonLette, "notifica da leggere", "notifiche da leggere")
        if (ctx.abbonamentiAttivi) ctx.abbonamento?.daysRemaining?.takeIf { it <= 7 && ctx.abbonamento.subscriptionType != SubscriptionType.FREE }?.let {
            voci += if (it <= 0) "l'abbonamento è scaduto" else "l'abbonamento scade tra ${plurale(it, "giorno", "giorni")}"
        }

        if (voci.isEmpty()) return "Tutto in ordine: non hai niente in sospeso. Puoi chiedermi \"ci sono turni nuovi?\"."
        return "Ecco cosa c'è da fare: " + voci.joinToString("; ") + "."
    }

    /** Turni ancora prenotabili: aperti, con posti liberi, non passati, senza già un match mio. */
    internal fun annunciNuovi(ctx: ContestoUtente, annunci: List<AnnuncioTurno>, oggi: LocalDate, tz: TimeZone): List<AnnuncioTurno> {
        val giaInMatch = ctx.matches.map { it.annuncioId }.toSet()
        return annunci
            .filter { it.stato == AnnuncioStato.APERTO && it.postiOccupati < it.quantitaMassima }
            .filter { it.dataTurno.toLocalDateTime(tz).date >= oggi && it.id !in giaInMatch }
            .sortedBy { it.dataTurno }
    }

    /** I primi 3 turni in una frase ("sabato 27 settembre 8:00-20:00, Farmacia Rossi (Milano), 30 €; ..."). */
    private fun elenco(annunci: List<AnnuncioTurno>, oggi: LocalDate, tz: TimeZone): String {
        val primi = annunci.take(3).joinToString("; ") { a ->
            buildString {
                append(formattaGiorno(a.dataTurno.toLocalDateTime(tz).date, oggi))
                append(" ${a.oraInizio}-${a.oraFine}")
                a.nomeCreatore?.let { append(", $it") }
                (a.localitaCreatore ?: a.zonaTestuale)?.takeIf { it.isNotBlank() }?.let { append(" ($it)") }
                a.compenso?.let { append(", ${formattaEuro(it)}") }
            }
        }
        val altri = annunci.size - 3
        return primi + if (altri > 0) "; e ${plurale(altri, "altro", "altri")}." else "."
    }

    private const val NON_CARICATI =
        "Non riesco a caricare i turni aperti in questo momento: controlla la connessione e riprova."

    /** Chi pubblica i turni che interessano a [mio]: le farmacie per i professionisti, farmacisti e infermieri per le farmacie. */
    internal fun controparti(mio: CreatoreTipo) = if (mio != CreatoreTipo.FARMACIA) "farmacie" else "farmacisti e infermieri"

    internal fun formattaEuro(v: Double): String {
        val centesimi = kotlin.math.round(v * 100).toLong()
        return if (centesimi % 100 == 0L) "${centesimi / 100} €"
        else "${centesimi / 100},${(centesimi % 100).toString().padStart(2, '0')} €"
    }

    internal data class Conteggi(val proposte: Int, val daConfermare: Int, val inAttesa: Int, val confermati: Int)

    /** Stessi conteggi della dashboard in HomeScreen: se cambiano là, vanno allineati qui. */
    internal fun conteggi(matches: List<Match>, mio: CreatoreTipo): Conteggi {
        val attivi = matches.filter { it.stato != MatchStato.RIFIUTATO && it.stato != MatchStato.ANNULLATO }
        return Conteggi(
            proposte = attivi.count { it.stato == MatchStato.PROPOSTO && it.propostoDa != mio },
            daConfermare = attivi.count { it.stato == MatchStato.ACCETTATO && it.propostoDa == mio },
            inAttesa = attivi.count {
                (it.stato == MatchStato.PROPOSTO && it.propostoDa == mio) ||
                    (it.stato == MatchStato.ACCETTATO && it.propostoDa != mio)
            },
            confermati = attivi.count { it.stato == MatchStato.CONFERMATO }
        )
    }

    private fun prossimoTurno(ctx: ContestoUtente, oggi: LocalDate, tz: TimeZone): String {
        fun Match.giorno(): LocalDate? = dataTurno?.toLocalDateTime(tz)?.date
        val futuri = ctx.matches.filter { (it.giorno() ?: return@filter false) >= oggi }
        val confermati = futuri.filter { it.stato == MatchStato.CONFERMATO }.sortedBy { it.dataTurno }
        val inSospeso = futuri.count { it.stato == MatchStato.PROPOSTO || it.stato == MatchStato.ACCETTATO }

        val prossimo = confermati.firstOrNull()
        if (prossimo == null) {
            return "Non hai turni confermati in programma." +
                if (inSospeso > 0) " Ne hai $inSospeso ancora da definire: li trovi nella Home." else ""
        }
        val dove = listOfNotNull(prossimo.nomeControparte, prossimo.cittaControparte).joinToString(", ")
        val quando = formattaGiorno(prossimo.giorno()!!, oggi)
        return buildString {
            append("Il tuo prossimo turno confermato è $quando")
            if (dove.isNotEmpty()) append(" ($dove)")
            append(".")
            val altri = confermati.size - 1
            if (altri > 0) append(" Dopo ne hai ${plurale(altri, "altro confermato", "altri confermati")}.")
            if (inSospeso > 0) append(" ${plurale(inSospeso, "turno è", "turni sono")} ancora da definire.")
        }
    }

    private fun statoTurni(ctx: ContestoUtente, testo: String = ""): String {
        val mio = ctx.ruolo?.creatoreTipo() ?: return "Accedi per vedere la situazione dei tuoi turni."
        // La farmacia: "proposte da infermieri?" (o lo spazio aperto nel selettore) guarda solo quella professione.
        val t = IntentClassifier.normalizza(testo)
        val professione = when {
            ctx.ruolo != UserRole.FARMACIA -> null
            t.contains("infermier") -> Professione.INFERMIERE
            t.contains("farmacist") -> Professione.FARMACISTA
            else -> ctx.professioneCercata
        }
        val matches = ctx.matches.filter { professione == null || it.professione == professione }
        val di = when (professione) {
            Professione.INFERMIERE -> " con gli infermieri"
            Professione.FARMACISTA -> " con i farmacisti"
            null -> ""
        }
        val (proposte, daConfermare, inAttesa, confermati) = conteggi(matches, mio)
        if (proposte + daConfermare + inAttesa + confermati == 0) {
            return "Al momento non hai proposte né turni in corso$di."
        }
        return "Situazione dei tuoi turni$di: ${plurale(proposte, "nuova proposta", "nuove proposte")}, " +
            "$daConfermare da confermare, $inAttesa in attesa dell'altra parte, " +
            "${plurale(confermati, "confermato", "confermati")}." +
            if (daConfermare > 0) " Scrivi \"turni da confermare\" per vederli." else ""
    }

    private fun notifiche(ctx: ContestoUtente): String {
        val nonLette = ctx.notifiche.filter { !it.isRead }.sortedByDescending { it.data }
        if (nonLette.isEmpty()) return "Non hai notifiche da leggere."
        val titoli = nonLette.take(3).joinToString("; ") { "«${it.titolo}»" }
        return "Hai ${plurale(nonLette.size, "notifica non letta", "notifiche non lette")}. " +
            (if (nonLette.size > 3) "Le più recenti: " else "") + "$titoli."
    }

    private fun abbonamento(ctx: ContestoUtente, tz: TimeZone): String {
        if (!ctx.abbonamentiAttivi) return "Al momento FarmaFreelance è gratuito: tutte le funzioni sono disponibili senza abbonamento."
        val s = ctx.abbonamento?.takeIf { it.userId.isNotBlank() }
            ?: return "Non ho ancora le informazioni sul tuo abbonamento: riprova tra poco o apri il Profilo."
        val scadenza = s.expiryDate?.let {
            Instant.fromEpochMilliseconds(it).toLocalDateTime(tz).date.let(::formattaData)
        }
        return when (s.subscriptionType) {
            SubscriptionType.TRIAL -> "Sei nel periodo di prova gratuita" +
                (s.daysRemaining?.let { ": ti ${if (it == 1) "resta 1 giorno" else "restano $it giorni"}" } ?: "") +
                (scadenza?.let { " (fino al $it)" } ?: "") + "."
            SubscriptionType.MONTHLY, SubscriptionType.YEARLY -> {
                val piano = if (s.subscriptionType == SubscriptionType.MONTHLY) "mensile" else "annuale"
                if (s.isValidPremium) "Hai l'abbonamento $piano attivo" + (scadenza?.let { " fino al $it" } ?: "") + "."
                else "Il tuo abbonamento $piano è scaduto" + (scadenza?.let { " il $it" } ?: "") + ": puoi rinnovarlo dal Profilo."
            }
            SubscriptionType.FREE -> "Hai l'account base gratuito. Puoi passare a Premium dal Profilo."
        }
    }

    private fun reputazione(ctx: ContestoUtente): String {
        val r = ctx.reputazione ?: return "Non ho ancora i dati sulla tua reputazione."
        val storico = "hai completato ${plurale(r.completedCount, "turno", "turni")} " +
            "e ne hai annullati ${r.cancelledCount}"
        if (!r.hasEnoughData) {
            return "Non ci sono ancora abbastanza turni per calcolare la reputazione: $storico."
        }
        val stelle = (r.stars * 10).toInt() / 10.0
        return "La tua reputazione è di $stelle stelle su 5: $storico." + when {
            r.discountEligible -> " Questo mese hai diritto allo sconto!"
            r.nextDiscountProgress > 0 ->
                " Ti ${if (r.nextDiscountProgress == 1) "manca 1 turno completato" else "mancano ${r.nextDiscountProgress} turni completati"} questo mese per lo sconto."
            else -> ""
        }
    }

    private const val AIUTO = "Sono Farmy, il tuo assistente, e funziono senza internet. Puoi chiedermi:\n" +
        "• \"quando è il mio prossimo turno?\", \"ho nuove proposte?\", \"ho notifiche?\"\n" +
        "• \"cosa devo fare oggi?\", \"ci sono turni nuovi vicino a me?\", \"ci sono turni dei miei preferiti?\"\n" +
        "• \"quando scade l'abbonamento?\", \"quante stelle ho?\"\n" +
        "• \"quali farmacie mi hanno pagato di più?\", \"chi paga di più nella mia zona?\", " +
        "\"ci sono emergenze vicino a me?\", \"fammi il riepilogo dei miei turni\"\n" +
        "• accettare un turno aperto: \"accetta il turno della Farmacia Rossi di sabato 7 novembre\"\n" +
        "• promemoria: \"ricordami ogni mattina i turni della giornata\", \"avvisami dei turni da confermare\", " +
        "\"quali promemoria ho?\"\n" +
        "Oppure darmi comandi: \"crea un turno domani dalle 8 alle 20\", \"cerca farmacie a Milano\", " +
        "\"cancella il turno del 15 ottobre\", \"mostrami le proposte\".\n" +
        "Posso gestire le proposte (\"accetta la proposta della Farmacia Rossi\", \"conferma il turno di sabato\", " +
        "\"disdici il turno con Mario Bianchi\"), mandarne una (\"proponi un turno alla Farmacia Rossi\"), " +
        "aprire un profilo e gestire i preferiti. Prima di cambiare qualcosa ti chiedo sempre conferma.\n" +
        "Posso avvisarti quando esce un turno che ti interessa: \"avvisami quando c'è un turno a Roma il sabato\".\n" +
        "Posso anche aprire qualsiasi pagina: \"apri i miei guadagni\", \"apri lo storico\", \"apri le impostazioni\", " +
        "\"apri i preferiti\", \"apri le notifiche\", \"apri il supporto\", \"apri la guida\"."

    private val GIORNI = listOf("lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica")
    private val MESI = listOf("gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio",
        "agosto", "settembre", "ottobre", "novembre", "dicembre")

    private fun formattaData(d: LocalDate) = "${d.dayOfMonth} ${MESI[d.monthNumber - 1]} ${d.year}"

    internal fun formattaGiorno(d: LocalDate, oggi: LocalDate): String {
        val base = "${GIORNI[d.dayOfWeek.ordinal]} ${d.dayOfMonth} ${MESI[d.monthNumber - 1]}"
        return when (d.toEpochDays() - oggi.toEpochDays()) {
            0 -> "oggi, $base"
            1 -> "domani, $base"
            else -> base
        }
    }

    internal fun plurale(n: Int, singolare: String, plurale: String) = if (n == 1) "1 $singolare" else "$n $plurale"

    internal fun UserRole.creatoreTipo(): CreatoreTipo? = when (this) {
        UserRole.FARMACIA -> CreatoreTipo.FARMACIA
        UserRole.FARMACISTA -> CreatoreTipo.FARMACISTA
        UserRole.INFERMIERE -> CreatoreTipo.INFERMIERE
        UserRole.ADMIN -> null
    }
}
